import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, OnDestroy, inject } from '@angular/core';
import { ButtonSize, ButtonState, ButtonType, INotification, JourneyStepStatus, JourneyStepType, UserRole } from '../../../assets/types/dtoTypes';
import { ThemeService } from '../services/theme.service';
import { Observable, Subscription } from 'rxjs';
import { BreakpointsService, MediaQueryResultMap, BIPPERZ_BREAKPOINTS } from '../services/breakpoint.service';
import {Router, RouterLink} from '@angular/router';
import { PATH } from '../../../assets/constants';
import { AuthenticationService } from '../services/authentication.service';
import { ExternalHeaderService } from './external-header.service';
import { ImageService } from '../services/image.service';
import { UnreadMessageService } from '../services/unread-message.service';
import { NotificationService } from '../services/notifications.service';
import { BackgroundUnreadService } from '../services/background-unread.service';
import {
  CompanyDashboardPageService,
} from '../../pages/main-pages/company/company-dashboard-page/company-dashboard-page.service';
import { JourneyService } from '../services/journey.service';
import {AsyncPipe, NgIf} from "@angular/common";
import {MatIconButton} from "@angular/material/button";
import {NotificationPanelComponent} from "../notification-panel/notification-panel.component";
import {TranslateModule} from "@ngx-translate/core";
import {MatIcon} from "@angular/material/icon";
import {ButtonComponent} from "../button/button.component";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {MatDialog} from "@angular/material/dialog";
import {BookConsultationModalComponent} from "../../modals/book-consultation-modal/book-consultation-modal.component";

@Component({
  selector: 'app-external-header',
  templateUrl: './external-header.component.html',
  standalone: true,
  imports: [
    NgIf,
    RouterLink,
    AsyncPipe,
    MatIcon,
    MatIconButton,
    NotificationPanelComponent,
    TranslateModule,
    ButtonComponent,
    MatMenu,
    MatMenuTrigger,
    MatMenuItem,
  ],
  styleUrls: ['./external-header.component.scss']
})
export class ExternalHeaderComponent implements OnInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private notificationService = inject(NotificationService);
  private authenticationService = inject(AuthenticationService);
  private companyService = inject(CompanyDashboardPageService);
  private unreadMessageService = inject(UnreadMessageService);
  private backgroundUnreadService = inject(BackgroundUnreadService);
  private imageService = inject(ImageService);
  private externalHeaderService = inject(ExternalHeaderService);
  private themeService = inject(ThemeService);
  private authService = inject(AuthenticationService);
  private breakpointsService = inject(BreakpointsService);
  private router = inject(Router);

  @Input() showLogo: boolean = true;

  @Input() isInboxPresent: boolean = false;

  @Input() isMobile: boolean = false;

  @Input() showButtons: boolean = true;

  @Input() showProfilePicture: boolean = false;

  @Input() isProfile: boolean = false;

  @Input() isCompanyProfile: boolean = false;

  @Input() isAgencyProfile: boolean = false;

  @Input() showConsultationCta: boolean = false;

  private dialog = inject(MatDialog);
  private journeyService = inject(JourneyService);

  readonly StepStatus = JourneyStepStatus;

  consultationStatus: JourneyStepStatus = JourneyStepStatus.AVAILABLE;

  openConsultationModal(): void {
    this.dialog.open(BookConsultationModalComponent, {
      maxWidth: '960px',
      width: '100%',
      panelClass: 'book-consultation-panel',
    });
  }

  @Output() openPopup: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() openRegisterPage = new EventEmitter<boolean>();

  totalUnreadMessages: number = 0;

  totalNotifications: number = 0;

  isImageLoaded = false;

  loggedRole: UserRole ;

  breakpoints$: Observable<MediaQueryResultMap>;

  currentTheme: string;

  imageUrl: string;

  protected readonly ButtonType = ButtonType;

  protected readonly ButtonSize = ButtonSize;

  protected readonly ButtonState = ButtonState;

  private themeSubscription: Subscription;

  showNotificationsPanel = false;

  id: string;

  companyId: string;

  userName: string;

  notifications: INotification[] = [];


  constructor() {
    this.breakpoints$ = this.breakpointsService.observe( [ BIPPERZ_BREAKPOINTS.s ] );
    this.loggedRole = this.authService.getUserRoleFromAccessToken();
  }

  navigateToPricing(): void {
    this.router.navigate([PATH.PRICING, this.id]);
  }

  navigateToHome(): void {
    const companyId = this.authenticationService.getCompanyIdFromAccessToken();
    const userId = this.authenticationService.getIdFromAccessToken();
    const role = this.authenticationService.getUserRoleFromAccessToken();
    if ( role === UserRole.USER ) {
      this.router.navigate( [ `${PATH.MAIN}/${userId}` ] );
    } else if ( role === UserRole.PARTNER ) {
      this.router.navigate( [ `${PATH.COMPANY_DASHBOARD}/${companyId}` ] );
    }
  }


  ngOnInit(): void {
    this.id = this.authService.getIdFromAccessToken();
    this.companyId = this.authService.getCompanyIdFromAccessToken();

    if (this.showConsultationCta) {
      this.journeyService.getMyJourney().subscribe({
        next: (journey) => {
          const step = journey.steps.find(s => s.type === JourneyStepType.CONSULTATION);
          if (step) {
            this.consultationStatus = step.status;
            this.cdr.detectChanges();
          }
        },
      });
    }


    const loggedId = this.companyId || this.id;
    if ( loggedId ) {
      this.backgroundUnreadService.startListening( loggedId );
      if ( 'Notification' in window && Notification.permission === 'default' ) {
        Notification.requestPermission();
      }
    }

    this.unreadMessageService.unreadCounts$.subscribe( () => {
      this.totalUnreadMessages = this.unreadMessageService.getTotalUnreadCount();
      this.cdr.detectChanges();
    } );

    if ( this.showProfilePicture ) {

      this.notificationService.getUnreadCount().subscribe( count => {
        this.totalNotifications = count;
        this.cdr.detectChanges();
      } );

      this.notificationService.notifications.subscribe( notifications => {
        this.notifications = notifications;
        this.cdr.detectChanges();
      } );

      this.notificationService.getNotifications( this.id ).subscribe( notifications => {
        this.notifications = notifications;
      } );

      this.notificationService.fetchNotifications( this.id );
      const role = this.authenticationService.getUserRoleFromAccessToken();
      if (role === UserRole.PARTNER || role === UserRole.AGENCY) {
        this.companyService.getCompanyData(this.companyId).subscribe(company => {
          this.userName = company.name;
          this.imageService.appendSasTokenToCompanyImage(company.logoUrl, company.id).subscribe((url) => {
            this.imageUrl = url;
            this.cdr.detectChanges();
          } );
        } );
      } else if (role === UserRole.USER ){
        this.externalHeaderService.getUser( this.id ).subscribe( user => {
          this.imageService.appendSasTokenToProfileImage( user ).subscribe( ( updatedUser ) => {
            this.imageUrl = updatedUser.profileImageUrl;
            this.userName = `${updatedUser.firstname} ${updatedUser.lastname}`;
            this.cdr.detectChanges();
          } );
        })
      } else {
        this.externalHeaderService.getUser( this.id ).subscribe( user => {
          this.imageService.appendSasTokenToProfileImage( user ).subscribe( ( updatedUser ) => {
            this.imageUrl = updatedUser.companyLogoUrl;
            this.cdr.detectChanges();
          } );
        } );
      }

    }

    this.currentTheme = this.themeService.getTheme();

    this.themeSubscription = this.themeService.theme$.subscribe( theme => {
      this.currentTheme = theme;
    } );
  }

  ngOnDestroy(): void {
    if ( this.themeSubscription ) {
      this.themeSubscription.unsubscribe();
    }
  }


  getLogo(): string {
    return  "../assets/new-logo-2.png";
  }

  isAuthenticated(): boolean {
    const token = this.authService.getAccessToken();
    return !!token;
  }

  openRegistrationPage() {
    this.openRegisterPage.emit();
  }

  openLoginPopup() {
    this.openPopup.emit( false );
  }

  navigateTo( page: string ) {
    this.router.navigate( [ `/${page}` ] );
  }

  navigateToMyProfile() {
    this.router.navigate( [ PATH.PROFIL, this.id ] );
  }

  navigateToCompanyHome() {
    this.router.navigate( [ PATH.COMPANY_DASHBOARD, this.companyId ] );
  }

  navigateToAgencyHome() {
    this.router.navigate( [ PATH.AGENCY_DASHBOARD] );
  }

  navigateToMyApplications() {
    this.router.navigate( [ PATH.APPLICATIONS_OVERVIEW, this.id ] );
  }

  navigateToJobs() {
    this.router.navigate( [ PATH.JOBS ] );
  }

  logout() {
    this.authService.logout();
  }

  onImageLoad() {
    this.isImageLoaded = true;
  }

  hasNotifications(): boolean {
    return this.totalNotifications > 0;
  }

  getRoleLabel(): string {
    switch (this.loggedRole) {
      case UserRole.PARTNER:  return 'Company';
      case UserRole.AGENCY:   return 'Agency';
      case UserRole.ADMIN:    return 'Administrator';
      default:                return 'Candidate';
    }
  }


  toggleNotificationsPanel(): void {
    this.showNotificationsPanel = !this.showNotificationsPanel;
    if ( this.showNotificationsPanel ) {
      this.notifications
        .filter( notification => !notification.isRead )
        .forEach( notification => this.notificationService.markAsRead( notification.id ) );
      this.cdr.detectChanges();
    }
  }
}
